/**
 * API Service - Supports BATCH Processing (Multiple PDFs)
 * v2.1 - Fire-and-Forget with Polling Support
 * 
 * ISSUE: The batch endpoint can take 5-10+ minutes for large files.
 * Browser connections timeout before completion (ERR_CONNECTION_CLOSED),
 * causing false "failed" status even though server continues processing.
 * 
 * SOLUTION: 
 * - Submit files and DON'T treat connection timeouts as failures
 * - Return expected appnums so UI can poll History API for results
 * - Use polling to check for completion
 */

const BATCH_API_ENDPOINT = 'https://tm-lossrun.enowclear360.com/process-loss-run-batch';
const HISTORY_API_ENDPOINT = 'https://tm-lossrun.enowclear360.com/api/v1/results';
const RECONCILIATION_API_BASE = 'https://tm-lossrun.enowclear360.com';
const ANALYSIS_ENDPOINT = 'https://api.elevatenow.tech/query';

export interface SubmissionResult {
  submitted: boolean;
  expectedAppnums: string[];
  pdfCount: number;
  message: string;
  workflow_id?: string;
  batch_id?: string;
  connectionClosed?: boolean;
  // These fields may be present if processing completes quickly
  LossRunMapper_Processed?: Array<{
    appnum?: string;
    augmented_policy?: any;
    message?: string;
    stats?: any;
  }>;
  timestamp?: string;
  files_uploaded?: any;
}

/**
 * Process loss run - BATCH endpoint
 *
 * IMPORTANT: This function may return BEFORE processing completes.
 * If the server takes too long, the browser connection will close,
 * but the server continues processing. In this case, we return
 * { submitted: true, connectionClosed: true } so the UI can poll.
 *
 * @param pdfFiles - Array of PDF files to process
 * @param jsonFile - Policy JSON file
 * @param accessToken - Optional Auth0 JWT access token
 * @returns Promise with submission info including expectedAppnums for polling
 */
async function processLossRun(pdfFiles: File[], jsonFile: File, accessToken?: string): Promise<SubmissionResult> {
  // Read JSON to validate and get appnums FIRST (before any network calls)
  const jsonText = await jsonFile.text();
  let policies: any[];

  try {
    const parsed = JSON.parse(jsonText);
    policies = Array.isArray(parsed) ? parsed : [parsed];
  } catch (e) {
    throw new Error('Invalid JSON format in policy file');
  }

  console.log(`📤 Submitting ${pdfFiles.length} PDF(s) with ${policies.length} policy(ies)`);

  // Validate: PDF count must match policy count
  if (pdfFiles.length !== policies.length) {
    throw new Error(`Count mismatch: ${pdfFiles.length} PDFs but ${policies.length} policies in JSON`);
  }

  // Extract appnums for polling (no validation)
  const expectedAppnums: string[] = [];
  for (const policy of policies) {
    const appnum = policy.appnum || policy.Application?.appnum;
    if (appnum) {
      expectedAppnums.push(appnum);
    }
  }

  console.log('📋 Expected Appnums:', expectedAppnums);

  // Create FormData for batch endpoint
  const formData = new FormData();

  // Add PDFs directly - NO validation
  for (const pdfFile of pdfFiles) {
    console.log(`📎 Adding PDF: ${pdfFile.name}`);
    formData.append('files[0][]', pdfFile);
  }

  // Backend expects array format
  // If original JSON is already an array, send it as-is to preserve formatting
  // If it's a single object, normalize it to array format
  let jsonFileToSend: File;

  const parsed = JSON.parse(jsonText);
  if (Array.isArray(parsed)) {
    // Already an array - send original file
    console.log(`📋 Sending original JSON file: ${policies.length} policy(ies) in array format`);
    jsonFileToSend = jsonFile;
  } else {
    // Single object - convert to array format for backend
    console.log(`📋 Converting single policy to array format for backend compatibility`);
    const normalizedJson = JSON.stringify(policies, null, 2);
    jsonFileToSend = new File(
      [normalizedJson],
      jsonFile.name,
      { type: 'application/json' }
    );
  }

  formData.append('files[1]', jsonFileToSend);

  console.log('📤 Sending to batch endpoint:', BATCH_API_ENDPOINT);

  // Prepare headers with optional Authorization
  const headers: HeadersInit = {};
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    console.log('✓ Including Authorization header with JWT token');
  } else {
    console.warn('⚠️ No access token provided - request may fail if endpoint is protected');
  }

  // Create AbortController with UNLIMITED timeout (actually 24 hours as practical limit)
  // This prevents browser from closing connection prematurely while waiting for server
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    console.log('⏱️ Request timeout after 24 hours');
    controller.abort();
  }, 24 * 60 * 60 * 1000); // 24 hours (effectively unlimited for batch processing)

  try {
    console.log('⏳ Waiting for complete server response (unlimited timeout)...');
    const response = await fetch(BATCH_API_ENDPOINT, {
      method: 'POST',
      headers,
      body: formData,
      signal: controller.signal
    });

    // Clear timeout since we got a response
    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      const errorMsg = errorData?.error || errorData?.details || `Upload failed: ${response.status}`;
      console.error('❌ API Error:', errorData);
      throw new Error(errorMsg);
    }

    // Got a complete response - processing finished
    const result = await response.json();
    console.log('✅ Got complete response from batch endpoint:', result);

    return {
      submitted: true,
      expectedAppnums,
      pdfCount: pdfFiles.length,
      message: 'Processing complete!',
      workflow_id: result.workflow_id,
      connectionClosed: false,
      ...result
    };

  } catch (fetchError: any) {
    // Clear timeout on error
    clearTimeout(timeoutId);

    // ALL errors (including connection timeouts) are now treated as FAILURES
    // No special handling for connection errors - if connection closes, it's a failure
    console.error('❌ Process error - connection lost or request failed:', fetchError);
    throw new Error(fetchError.message || 'Failed to process files - connection lost');
  }
}

/**
 * Poll for a specific workflow result
 * Checks History API for records matching the expected appnums
 * 
 * @param expectedAppnums - Array of appnums we're looking for
 * @param maxAttempts - Maximum polling attempts
 * @param intervalMs - Milliseconds between attempts
 */
async function pollForResult(
  expectedAppnums: string[],
  maxAttempts: number = 60,
  intervalMs: number = 10000
): Promise<any | null> {
  console.log(`🔄 Starting polling for appnums: ${expectedAppnums.join(', ')}`);

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      console.log(`🔄 Poll attempt ${attempt}/${maxAttempts}`);

      const response = await getHistory();
      const records = response.results;

      // Look for a record that contains our appnums
      for (const record of records) {
        const recordAppnums = extractAppnumsFromRecord(record);

        // Check if this record contains ALL our expected appnums
        const hasAllAppnums = expectedAppnums.every(appnum =>
          recordAppnums.includes(appnum)
        );

        if (hasAllAppnums) {
          console.log('✅ Found matching record!', record.workflow_id);
          return record;
        }
      }

      console.log(`⏳ Not found yet, waiting ${intervalMs / 1000}s...`);
      await new Promise(resolve => setTimeout(resolve, intervalMs));

    } catch (error) {
      console.error(`❌ Poll attempt ${attempt} failed:`, error);
      // Continue polling despite errors
      await new Promise(resolve => setTimeout(resolve, intervalMs));
    }
  }

  console.log('⚠️ Polling timeout - record not found');
  return null;
}

/**
 * Extract appnums from a history record
 */
function extractAppnumsFromRecord(record: any): string[] {
  const appnums: string[] = [];

  // Check LossRunMapper_Processed array
  const processed = record.LossRunMapper_Processed;
  if (Array.isArray(processed)) {
    for (const item of processed) {
      if (item.appnum) {
        appnums.push(item.appnum);
      }
    }
  }

  // Also check workflow_id format (sometimes contains appnum)
  if (record.workflow_id) {
    // Some workflow IDs might encode the appnum
  }

  return appnums;
}

export interface PaginationMeta {
  current_page: number;
  page_size: number;
  total_count: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
}

export interface HistoryResponse {
  results: any[];
  pagination: PaginationMeta | null;
}

/**
 * Get history - returns Paginated response
 * @param accessToken - Optional Auth0 JWT access token
 * @param page - Page number (default 1)
 * @param pageSize - Page size (default 5)
 */
async function getHistory(accessToken?: string, page: number = 1, pageSize: number = 5): Promise<HistoryResponse> {
  try {
    const url = `${HISTORY_API_ENDPOINT}?workflow_id=*&page=${page}&page_size=${pageSize}`;
    console.log('📚 Fetching history from:', url);

    // Prepare headers with optional Authorization
    const headers: HeadersInit = {};
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
      console.log('✓ Including Authorization header for history request');
    }

    const response = await fetch(url, {
      headers
    });

    if (!response.ok) {
      throw new Error(`History failed: ${response.status}`);
    }

    const data: any = await response.json();
    console.log('📚 Raw history response:', data);

    let results: any[] = [];
    let pagination: PaginationMeta | null = null;

    if (data.results && Array.isArray(data.results)) {
      results = data.results;
      pagination = data.pagination || null;
    } else if (Array.isArray(data)) {
      // Fallback for old API response (if ever needed)
      results = data;
    } else if (data.data && Array.isArray(data.data)) {
      results = data.data;
    } else {
      console.warn('⚠️ Unexpected history response format:', data);
      results = [];
    }

    console.log(`📚 Returning ${results.length} records (Page ${pagination?.current_page || 1})`);
    return { results, pagination };

  } catch (error) {
    console.error('❌ History error:', error);
    throw error;
  }
}

/**
 * Query AI analysis endpoint
 */
async function queryAnalysis(question: string, context: any, threadId?: number): Promise<any> {
  try {
    const payload: any = {
      question,
      context: JSON.stringify(context)
    };

    if (threadId) {
      payload.thread_id = threadId;
    }

    const response = await fetch(ANALYSIS_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(`Analysis failed: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('❌ Analysis error:', error);
    throw error;
  }
}

/**
 * Get list of reconciliation reports for a batch
 * @param batchId - Batch ID
 * @param accessToken - Optional Auth0 JWT access token
 */
async function getReconciliationReports(batchId: string, accessToken?: string): Promise<any> {
  try {
    // Prepare headers with optional Authorization
    const headers: HeadersInit = {};
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
      console.log('✓ Including Authorization header for reconciliation reports request');
    }

    const response = await fetch(`${RECONCILIATION_API_BASE}/api/batch/${batchId}/reconciliation-reports`, {
      headers
    });

    if (!response.ok) {
      throw new Error(`Failed to get reconciliation reports: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('❌ Error getting reconciliation reports:', error);
    throw error;
  }
}

/**
 * Download a reconciliation report
 * Returns a blob URL that can be used to download the file
 * @param batchId - Batch ID
 * @param filename - Report filename
 * @param accessToken - Optional Auth0 JWT access token
 */
async function downloadReconciliationReport(batchId: string, filename: string, accessToken?: string): Promise<void> {
  try {
    // Prepare headers with optional Authorization
    const headers: HeadersInit = {};
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
      console.log('✓ Including Authorization header for report download');
    }

    const response = await fetch(`${RECONCILIATION_API_BASE}/api/reconciliation-report/${batchId}/${filename}`, {
      headers
    });

    if (!response.ok) {
      throw new Error(`Failed to download report: ${response.status}`);
    }

    // Get the blob
    const blob = await response.blob();

    // Create download link
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();

    // Cleanup
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);

  } catch (error) {
    console.error('❌ Error downloading reconciliation report:', error);
    throw error;
  }
}

export const apiService = {
  processLossRun,
  pollForResult,
  getHistory,
  queryAnalysis,
  getReconciliationReports,
  downloadReconciliationReport
};